import { useMemo, useState } from 'react';
import { Can } from '@/components/common/Can';
import { BILLING_PERMISSIONS } from '@/constants/permissions';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { AreaRouteCell } from '@/components/common/AreaRouteCell';
import { createActionsColumn, createSerialColumn, DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { RowActionButton } from '@/components/common/RowActions';
import { TableTabs } from '@/components/common/TableTabs';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import {
  dcrApprovalInfiniteQueryOptions,
  derApprovalInfiniteQueryOptions,
  expenseTypeQueryOptions,
} from '../api/billing.api';
import { AmendDialog, type AmendTarget } from '../components/AmendDialog';
import { BillingFilterPanel } from '../components/BillingFilterPanel';
import { getDistributorNameColumn } from '../components/DistributorColumns';
import { getQtyColumn } from '../components/QtyColumn';
import { VendorCell } from '../components/VendorCell';
import {
  BILLING_ENTRY_TABS,
  BILLING_ENTRY_TABS_LABEL,
  BILLING_SEARCH_PLACEHOLDER,
  type BillingEntryTab,
} from '../constants/billing.constants';
import { useApprovalFilters } from '../hooks/useApprovalFilters';
import { getExpenseTypeFilterField, getWorkTypeFilterField } from '../utils/billingFilterFields';
import { getClaimStatCards } from '../utils/billingStatCards';
import { useAmendDcr, useAmendDer } from '../hooks/useAmendEntry';
import type { OfflineDcrRow, OfflineDerRow } from '../types/billing.types';

export function CostAmendmentPage() {
  const controller = useApprovalFilters('amendment');
  const [tab, setTab] = useState<BillingEntryTab>('dcr');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [target, setTarget] = useState<AmendTarget | null>(null);

  const isDcrTab = tab === 'dcr';
  const dcrQuery = useInfiniteTableQuery({
    ...dcrApprovalInfiniteQueryOptions(controller.dcrParams),
    enabled: isDcrTab,
  });
  const derQuery = useInfiniteTableQuery({
    ...derApprovalInfiniteQueryOptions(controller.derParams),
    enabled: !isDcrTab,
  });
  const { data: expenseTypes } = useQuery(expenseTypeQueryOptions());

  const amendDcr = useAmendDcr();
  const amendDer = useAmendDer();

  const { search } = controller.dcrParams;
  const dcrColumns = useMemo<ColumnDef<OfflineDcrRow, unknown>[]>(
    () => [
      createSerialColumn<OfflineDcrRow>(),
      getDistributorNameColumn<OfflineDcrRow>(search),
      {
        id: 'date',
        header: 'DCR Date',
        cell: ({ row }) => (
          <span className="text-sm">{formatDisplayDate(row.original.dcrDate)}</span>
        ),
      },
      {
        id: 'workType',
        header: 'Work Type',
        cell: ({ row }) => (
          <WorkTypeBadge code={row.original.workTypeCode} name={row.original.workType} />
        ),
      },
      getQtyColumn(),
      {
        id: 'area',
        header: 'Area From – To',
        cell: ({ row }) => (
          <AreaRouteCell
            fromName={row.original.areaFrom}
            toName={row.original.areaTo}
            comments={null}
            userId={row.original.userId}
            personName={row.original.name}
            dcrDate={row.original.dcrDate}
          />
        ),
      },
      {
        accessorKey: 'fareAmount',
        header: 'Claimed Fare (Tk)',
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{formatNumber(row.original.fareAmount)}</span>
        ),
      },
      createActionsColumn<OfflineDcrRow>((dcr) => (
        <Can code={BILLING_PERMISSIONS.COST_AMENDMENT_AMEND}>
          <RowActionButton
            label="Correct"
            ariaLabel={`Correct the DCR fare of ${dcr.name}`}
            onClick={() =>
              setTarget({
                id: dcr.recordId,
                who: dcr.name,
                what: `${dcr.workTypeCode ?? 'DCR'} on ${formatDisplayDate(dcr.dcrDate)}`,
                claimed: dcr.fareAmount,
              })
            }
          />
        </Can>
      )),
    ],
    [search],
  );

  const derColumns = useMemo<ColumnDef<OfflineDerRow, unknown>[]>(
    () => [
      createSerialColumn<OfflineDerRow>(),
      getDistributorNameColumn<OfflineDerRow>(search),
      {
        id: 'date',
        header: 'Expense Date',
        cell: ({ row }) => (
          <span className="text-sm">{formatDisplayDate(row.original.expenseDate)}</span>
        ),
      },
      {
        accessorKey: 'expenseType',
        header: 'Expense Type',
        cell: ({ row }) => <span className="text-sm">{row.original.expenseType ?? '—'}</span>,
      },
      {
        accessorKey: 'vendor',
        header: 'Vendor',
        cell: ({ row }) => (
          <VendorCell
            vendor={row.original.vendor}
            lat={row.original.vendorLat}
            lon={row.original.vendorLon}
          />
        ),
      },
      {
        accessorKey: 'amount',
        header: 'Claimed (Tk)',
        cell: ({ row }) => (
          <span className="font-medium tabular-nums">{formatNumber(row.original.amount)}</span>
        ),
      },
      createActionsColumn<OfflineDerRow>((der) => (
        <Can code={BILLING_PERMISSIONS.COST_AMENDMENT_AMEND}>
          <RowActionButton
            label="Correct"
            ariaLabel={`Correct the expense of ${der.name}`}
            onClick={() =>
              setTarget({
                id: der.detailId,
                who: der.name,
                what: der.expenseType ?? 'expense',
                claimed: der.amount,
              })
            }
          />
        </Can>
      )),
    ],
    [search],
  );

  function handleSubmit(amount: number, remarks: string) {
    if (!target) return;

    const payload = remarks.trim() === '' ? {} : { remarks: remarks.trim() };
    const onSettled = () => setTarget(null);

    if (isDcrTab) {
      amendDcr.mutate(
        { recordId: target.id, payload: { fareAmount: amount, ...payload } },
        { onSettled },
      );
    } else {
      amendDer.mutate({ detailId: target.id, payload: { amount, ...payload } }, { onSettled });
    }
  }

  const activeQuery = isDcrTab ? dcrQuery : derQuery;
  const activeStats = getClaimStatCards(isDcrTab ? 'dcr' : 'der', activeQuery.firstPageMeta);
  const activeTable = isDcrTab ? (
    <DataTable
      data={dcrQuery.rows}
      columns={dcrColumns}
      isLoading={dcrQuery.isLoading}
      className="lg:flex-1"
      stickyColumnId="name"
      emptyMessage="Nothing open to correct"
      emptyHint="Only entries that have not been approved can be changed."
      {...dcrQuery.loadMoreProps}
    />
  ) : (
    <DataTable
      data={derQuery.rows}
      columns={derColumns}
      isLoading={derQuery.isLoading}
      className="lg:flex-1"
      stickyColumnId="name"
      emptyMessage="Nothing open to correct"
      emptyHint="Only expenses that have not been approved can be changed."
      {...derQuery.loadMoreProps}
    />
  );

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR Cost Amendment"
        search={{
          value: controller.searchText,
          onChange: controller.setSearchText,
          placeholder: BILLING_SEARCH_PLACEHOLDER,
        }}
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={controller.period.dateFrom}
            dateTo={controller.period.dateTo}
          />
        }
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <BillingFilterPanel
          filters={controller}
          fieldsAfterGeo={[
            isDcrTab ? getWorkTypeFilterField() : getExpenseTypeFilterField(expenseTypes),
          ]}
        />
      </FilterPanelCollapse>

      <TableTabs
        tabs={BILLING_ENTRY_TABS}
        activeTabId={tab}
        onTabChange={setTab}
        ariaLabel={BILLING_ENTRY_TABS_LABEL}
      />

      {activeQuery.loadError ? (
        <ErrorState
          error={activeQuery.loadError}
          fallbackTitle="Could not load the entries"
          onRetry={() => void activeQuery.refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid isLoading={activeQuery.isLoading} stats={activeStats} />

          {activeTable}
        </>
      )}

      <AmendDialog
        target={target}
        isPending={amendDcr.isPending || amendDer.isPending}
        onClose={() => setTarget(null)}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
